import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Wrench, Globe, Users, ArrowRight, Target, Zap, HandshakeIcon } from 'lucide-react';
import axios from 'axios';
import SEO from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AboutPage() {
  const { t, language, lp } = useLanguage();
  const [team, setTeam] = useState([]);

  useEffect(() => {
    axios.get(`${API}/team`).then(r => setTeam(r.data)).catch(() => {});
  }, []);

  const getLocalized = (item, field) => item[`${field}_${language}`] || item[`${field}_de`] || '';

  const values = [
    { icon: Target, title: t('about.value1.title'), text: t('about.value1.text'), color: 'bg-blue-50 text-[#066ce7]' },
    { icon: Globe, title: t('about.value2.title'), text: t('about.value2.text'), color: 'bg-emerald-50 text-emerald-600' },
    { icon: HandshakeIcon, title: t('about.value3.title'), text: t('about.value3.text'), color: 'bg-amber-50 text-amber-600' },
    { icon: Zap, title: language === 'de' ? 'Schnelligkeit' : language === 'en' ? 'Speed' : 'Rapidez',
      text: language === 'de' ? 'Effiziente Umsetzung mit kurzen Reaktionszeiten.' : language === 'en' ? 'Efficient implementation with short response times.' : 'Implementación eficiente con tiempos de respuesta cortos.',
      color: 'bg-violet-50 text-violet-600' },
  ];

  return (
    <div data-testid="about-page">
      <SEO page="about" />
      {/* Hero */}
      <section className="relative py-20 md:py-28 bg-[#0F172A]">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '40px 40px'}} />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 md:px-8 text-center">
          <span className="inline-block text-[#3b8dff] text-sm font-semibold uppercase tracking-wider mb-4">{t('about.tag')}</span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mb-6" data-testid="about-title">{t('about.title')}</h1>
          <p className="text-slate-400 text-lg max-w-2xl mx-auto">{t('about.heroSubtitle')}</p>
        </div>
      </section>

      {/* Story */}
      <section className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-6">
              <h2 className="text-3xl font-bold text-[#0F172A]">
                {language === 'de' ? 'Unsere Geschichte' : language === 'en' ? 'Our Story' : 'Nuestra Historia'}
              </h2>
              <p className="text-slate-600 leading-relaxed">{t('about.text1')}</p>
              <p className="text-slate-600 leading-relaxed">{t('about.text2')}</p>
              <div className="bg-blue-50 border-l-4 border-[#066ce7] p-5 rounded-r-lg">
                <p className="text-[#0F172A] font-semibold text-sm">
                  {language === 'de' ? '"Lieferung, Montage und Projektsteuerung aus einer Hand – das ist unser Versprechen."' :
                   language === 'en' ? '"Delivery, assembly and project management from a single source – that is our promise."' :
                   '"Entrega, montaje y gestión de proyectos de una sola fuente – esa es nuestra promesa."'}
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <img src="/api/uploads/hero_main.jpg" alt="ITCS Project" className="rounded-xl w-full h-56 object-cover" />
              <img src="/api/uploads/service_largescale.jpg" alt="ITCS Hall" className="rounded-xl w-full h-56 object-cover mt-8" />
              <img src="/api/uploads/service_framework.jpg" alt="ITCS Team" className="rounded-xl w-full h-56 object-cover" />
              <img src="/api/uploads/team_montage1.jpg" alt="ITCS Work" className="rounded-xl w-full h-56 object-cover mt-8" />
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 md:py-32 blueprint-bg">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <h2 className="text-3xl font-bold text-center text-[#0F172A] mb-16">
            {language === 'de' ? 'Unsere Stärken' : language === 'en' ? 'Our Strengths' : 'Nuestras Fortalezas'}
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((v, i) => (
              <div key={i} className="bg-white border border-slate-200 rounded-xl p-7 service-card-hover" data-testid={`value-card-${i}`}>
                <div className={`w-12 h-12 rounded-lg ${v.color} flex items-center justify-center mb-5`}>
                  <v.icon className="w-6 h-6" />
                </div>
                <h3 className="font-bold text-[#0F172A] mb-2">{v.title}</h3>
                <p className="text-slate-500 text-sm leading-relaxed">{v.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      {team.length > 0 && (
        <section className="py-20 md:py-32 bg-white">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <h2 className="text-3xl font-bold text-center text-[#0F172A] mb-12">
              {language === 'de' ? 'Unser Team' : language === 'en' ? 'Our Team' : 'Nuestro Equipo'}
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-4xl mx-auto">
              {team.map((member, i) => (
                <div key={member.id || i} className="text-center" data-testid={`team-member-${i}`}>
                  <img src={member.image || 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=400'} alt={member.name} className="w-32 h-32 rounded-full object-cover mx-auto mb-4 border-4 border-blue-50" />
                  <h3 className="font-bold text-[#0F172A]">{member.name}</h3>
                  <p className="text-[#066ce7] text-sm">{getLocalized(member, 'position')}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-16 bg-[#066ce7]">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-6">{t('cta.title')}</h2>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to={lp('/planner')}>
              <Button className="bg-white text-[#066ce7] hover:bg-blue-50 px-8 py-3 font-semibold shadow-xl" data-testid="about-cta-planner">
                {t('cta.btn1')} <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to={lp('/contact')}>
              <Button variant="outline" className="border-2 border-white/40 text-white hover:bg-white/10 px-8 py-3 font-semibold" data-testid="about-cta-contact">
                {t('cta.btn2')}
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
